import React from "react";
import "./BreachDetails.scss";
import { FormatUSD } from "../../utilities/string";
import dayjs from "dayjs";

const BreachDetails = ({ breachData }) => {
  if (!breachData || !breachData.data) {
    return null;
  }

  const {
    category,
    action,
    admin_comment,
    created_at,
    meta_data: meta = {},
  } = breachData.data;

  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD MMM YYYY, HH:mm:ss");
  };

  // Safe checks for arrays
  const openPositions = meta.open_positions || [];
  const closedTrades = meta.contributing_closed_trades || [];

  return (
    <div className="breachDetailsContainer">
      <div className="breachHeaderAlert">
        <div className="breachTitleWrapper">
          <span className="breachStatusBadge">BREACH DETECTED</span>
          <h4>
            {category?.replace(/_/g, " ") || "BREACH NOTIFICATION"}{" "}
            {action && action !== "OTHER" ? `(${action})` : ""}
          </h4>
        </div>
        <div className="breachDate">
          {created_at ? formatDate(created_at) : "N/A"}
        </div>
      </div>

      <div className="breachContentCard">

        <div className="breachMetaGrid">
          {meta.reason && (
            <div className="metricCard reason">
              <span className="metricLabel">Reason</span>
              <span className="metricValue">
                {meta.reason
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </span>
            </div>
          )}
          {meta.agent && (
            <div className="metricCard agent">
              <span className="metricLabel">Agent</span>
              <span className="metricValue">{meta.agent}</span>
            </div>
          )}
          {meta.platform && (
            <div className="metricCard platform">
              <span className="metricLabel">Platform</span>
              <span className="metricValue">
                {meta.platform.toUpperCase()}
              </span>
            </div>
          )}
          {meta.asset_key && (
            <div className="metricCard asset">
              <span className="metricLabel">Asset Trigger</span>
              <span className="metricValue">{meta.asset_key}</span>
            </div>
          )}
          <div className="metricCard">
            <span className="metricLabel">Account Size</span>
            <span className="metricValue">{FormatUSD(meta.account_size)}</span>
          </div>
          <div className="metricCard">
            <span className="metricLabel">Loss Threshold</span>
            <span className="metricValue">{FormatUSD(meta.threshold)}</span>
          </div>
          <div className="metricCard highlightDanger">
            <span className="metricLabel">Breach Loss</span>
            <span className="metricValue dangerText">
              {FormatUSD(meta.breach_loss)}
            </span>
          </div>
        </div>

        {/* Open Positions at Breach */}
        <div className="breachTableSection">
          <h5>Open Positions at Time of Breach</h5>
          {openPositions.length > 0 ? (
            <div className="breachTableWrapper">
              <table className="breachDetailsTable">
                <thead>
                  <tr>
                    <th>Position ID</th>
                    <th>Action</th>
                    <th>Symbol</th>
                    <th>Volume</th>
                    <th>Open Price</th>
                    <th>SL</th>
                    <th>TP</th>
                    <th>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {openPositions.map((pos, index) => {
                    const ws = pos.ws_event || {};
                    const profitVal = ws.profit ?? pos.profit ?? 0;
                    return (
                      <tr key={pos.position_id || index}>
                        <td>{pos.position_id || "N/A"}</td>
                        <td>
                          <span
                            className={`sideBadge ${
                              pos.action?.toLowerCase() === "buy" ? "buy" : "sell"
                            }`}
                          >
                            {pos.action?.toUpperCase() || "N/A"}
                          </span>
                        </td>
                        <td>{ws.symbol || pos.symbol || "N/A"}</td>
                        <td>{pos.volume ?? "N/A"}</td>
                        <td>{pos.open_price ?? "N/A"}</td>
                        <td>{ws.sl || "N/A"}</td>
                        <td>{ws.tp || "N/A"}</td>
                        <td className={profitVal >= 0 ? "profitText" : "lossText"}>
                          {FormatUSD(profitVal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="noDataMsg">No open positions active at the time of breach.</p>
          )}
        </div>

        {/* Contributing Closed Trades */}
        {closedTrades.length > 0 && (
          <div className="breachTableSection">
            <h5>Contributing Closed Trades</h5>
            <div className="breachTableWrapper">
              <table className="breachDetailsTable">
                <thead>
                  <tr>
                    <th>Position ID</th>
                    <th>Closed Date/Time</th>
                    <th>Age</th>
                    <th>Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {closedTrades.map((trade, index) => {
                    const lossVal = trade.loss ?? 0;
                    const closedDate = trade.closed_ts
                      ? dayjs.unix(trade.closed_ts).format("DD MMM YYYY, HH:mm:ss")
                      : "N/A";
                    return (
                      <tr key={trade.position_id || index}>
                        <td>{trade.position_id || "N/A"}</td>
                        <td>{closedDate}</td>
                        <td>{trade.age_sec !== undefined ? `${trade.age_sec}s` : "N/A"}</td>
                        <td className="lossText">
                          -{FormatUSD(lossVal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BreachDetails;
